import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Modal,
  message,
  Table,
  Card,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import {
  getWorkflowDefinition,
  deleteWorkflowDefinition,
  activateWorkflowDefinition,
  deactivateWorkflowDefinition,
  type WorkflowDefinition,
  type WorkflowStepDefinition,
} from '@/api/workflowApi';
import {
  getWorkflowInstances,
  type WorkflowInstance,
} from '@/api/workflowApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  draft: 'default',
  active: 'green',
  inactive: 'orange',
};

const TRIGGER_COLORS: Record<string, string> = {
  manual: 'blue',
  event: 'purple',
  scheduled: 'cyan',
};

const STEP_TYPE_COLORS: Record<string, string> = {
  action: 'blue',
  condition: 'gold',
  delay: 'cyan',
  notification: 'purple',
};

const INSTANCE_STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'green',
  failed: 'red',
  cancelled: 'orange',
};

export default function WorkflowDefinitionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canWrite = hasPermission('workflows', 'write');
  const canDelete = hasPermission('workflows', 'delete');

  const [definition, setDefinition] = useState<WorkflowDefinition | null>(null);
  const [instances, setInstances] = useState<WorkflowInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [instanceLoading, setInstanceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadInstances = useCallback(
    (defId: string) => {
      setInstanceLoading(true);
      getWorkflowInstances({ definition_id: defId, size: 50 })
        .then((res) => setInstances(res.items))
        .catch(() => setInstances([]))
        .finally(() => setInstanceLoading(false));
    },
    []
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    getWorkflowDefinition(id)
      .then((def) => {
        setDefinition(def);
        loadInstances(id);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('app.not_found'))
      )
      .finally(() => setLoading(false));
  }, [id, t, loadInstances]);

  const handleDelete = useCallback(() => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('workflows.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteWorkflowDefinition(id);
          message.success(t('workflows.delete_success'));
          navigate('/workflows');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  }, [id, t, navigate]);

  const handleActivate = useCallback(async () => {
    if (!id) return;
    try {
      const updated = await activateWorkflowDefinition(id);
      setDefinition(updated);
      message.success(t('workflows.activate_success'));
    } catch {
      message.error(t('app.error'));
    }
  }, [id, t]);

  const handleDeactivate = useCallback(async () => {
    if (!id) return;
    try {
      const updated = await deactivateWorkflowDefinition(id);
      setDefinition(updated);
      message.success(t('workflows.deactivate_success'));
    } catch {
      message.error(t('app.error'));
    }
  }, [id, t]);

  const stepColumns: ColumnsType<WorkflowStepDefinition> = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'step_index',
        key: 'step_index',
        width: 60,
        align: 'center',
      },
      {
        title: t('workflows.field.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
      },
      {
        title: t('workflows.field.step_type'),
        dataIndex: 'type',
        key: 'type',
        width: 120,
        render: (type: string) => (
          <Tag color={STEP_TYPE_COLORS[type] ?? 'default'}>
            {t(`workflows.step_type_options.${type}`, type)}
          </Tag>
        ),
      },
      {
        title: t('workflows.field.step_action'),
        dataIndex: 'action',
        key: 'action',
        width: 150,
        ellipsis: true,
      },
      {
        title: t('workflows.field.on_success'),
        dataIndex: 'on_success',
        key: 'on_success',
        width: 100,
        align: 'center',
        render: (val: number | null) => (val !== null ? `${t('workflows.field.step')} ${val}` : '-'),
      },
      {
        title: t('workflows.field.on_failure'),
        dataIndex: 'on_failure',
        key: 'on_failure',
        width: 100,
        align: 'center',
        render: (val: number | null) => (val !== null ? `${t('workflows.field.step')} ${val}` : '-'),
      },
    ],
    [t]
  );

  const instanceColumns: ColumnsType<WorkflowInstance> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 200,
        ellipsis: true,
        render: (instId: string) => (
          <a onClick={() => navigate(`/workflows/instances/${instId}`)}>{instId}</a>
        ),
      },
      {
        title: t('workflows.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (status: string) => (
          <Tag color={INSTANCE_STATUS_COLORS[status] ?? 'default'}>
            {t(`workflows.instance_status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('workflows.field.current_step_index'),
        dataIndex: 'current_step_index',
        key: 'current_step_index',
        width: 100,
        align: 'center',
      },
      {
        title: t('workflows.field.started_at'),
        dataIndex: 'started_at',
        key: 'started_at',
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('workflows.field.completed_at'),
        dataIndex: 'completed_at',
        key: 'completed_at',
        width: 170,
        render: (val: string | null) => (val ? new Date(val).toLocaleString() : '-'),
      },
      {
        title: t('workflows.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        width: 170,
        render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
      },
    ],
    [t, navigate]
  );

  if (loading)
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );

  if (error || !definition) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/workflows')}
        >
          {t('app.back')}
        </Button>
        <Alert
          type="error"
          message={error || t('app.not_found')}
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/workflows')}
        >
          {t('app.back')}
        </Button>
        {canWrite && (
          <>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/workflows/definitions/${id}/edit`)}
            >
              {t('app.edit')}
            </Button>
            {definition.status !== 'active' && (
              <Button
                icon={<PlayCircleOutlined />}
                onClick={handleActivate}
              >
                {t('workflows.action.activate')}
              </Button>
            )}
            {definition.status === 'active' && (
              <Button
                icon={<PauseCircleOutlined />}
                onClick={handleDeactivate}
              >
                {t('workflows.action.deactivate')}
              </Button>
            )}
          </>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>{definition.name}</h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('workflows.field.name')}>
          {definition.name}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.version')}>
          v{definition.version}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.trigger_type')}>
          <Tag color={TRIGGER_COLORS[definition.trigger_type] ?? 'default'}>
            {t(`workflows.trigger_type_options.${definition.trigger_type}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.status')}>
          <Tag color={STATUS_COLORS[definition.status] ?? 'default'}>
            {t(`workflows.status_options.${definition.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.description')} span={2}>
          {definition.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.tags')} span={2}>
          {definition.tags?.length > 0
            ? definition.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>

        {/* Audit */}
        <Descriptions.Item label={t('workflows.field.created_by')}>
          {definition.created_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.created_at')}>
          {definition.created_at ? new Date(definition.created_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.updated_by')}>
          {definition.updated_by || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('workflows.field.updated_at')}>
          {definition.updated_at ? new Date(definition.updated_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>

      {/* Steps */}
      <Card
        title={t('workflows.field.steps') + ` (${definition.steps?.length ?? 0})`}
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Table<WorkflowStepDefinition>
          columns={stepColumns}
          dataSource={definition.steps ?? []}
          rowKey="step_index"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Instances History */}
      <div style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{t('workflows.instances_title')}</h3>
          {canWrite && definition.status === 'active' && (
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/workflows/instances/new?definition_id=${id}`)}
            >
              {t('workflows.action.run')}
            </Button>
          )}
        </Space>
        <Table<WorkflowInstance>
          columns={instanceColumns}
          dataSource={instances}
          rowKey="id"
          loading={instanceLoading}
          pagination={false}
          size="small"
        />
      </div>
    </div>
  );
}
